import GhHtmlElement from '@gudhub/gh-html-element';
import html from './callProfile.html';
import { getOrgContacts } from '../../contacts.js';
import { getCallHistory, getRecordingUrl } from './callHistory.request.js';
import { request as sipTrunksRequest } from '../../../user/voip_telephony/sip_trunks/sipTrunks.request.js';
import './callProfile.scss';

// Digit/letters pairs for the keypad grid - same layout as the legacy
// twilio-phone-panel (gh_elements/twilio_phone/twilio_phone_panel.js), which
// this whole 5-tab panel is modeled on.
// Recording player toggle icons - swapped in wireHistoryPlayer() as the
// <audio> element's actual play/pause state changes. Kept as JS constants
// (rather than template-driven) since they're applied via direct DOM
// manipulation, not re-render.
const PLAY_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>';
const PAUSE_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>';

const KEYPAD_DIGITS = [
    { number: '1', letters: '' },
    { number: '2', letters: 'ABC' },
    { number: '3', letters: 'DEF' },
    { number: '4', letters: 'GHI' },
    { number: '5', letters: 'JKL' },
    { number: '6', letters: 'MNO' },
    { number: '7', letters: 'PQRS' },
    { number: '8', letters: 'TUV' },
    { number: '9', letters: 'WXYZ' },
    { number: '*', letters: '' },
    { number: '0', letters: '+' },
    { number: '#', letters: '' }
];

// Strips everything but digits, so filterCallHistory's item-phone-number
// scoping isn't tripped up by "+"/spaces/dashes formatting differences
// between an item's stored phone field value and the call log's from/to.
function normalizeNumber(value) {
    return String(value || '').replace(/\D/g, '');
}

class GhCallProfile extends GhHtmlElement {

    //********************* CONNECTED CALLBACK ********************************//
    // <gh-call-profile> is a global, attribute-less element (like <gh-calls>) -
    // the base class's attributeChangedCallback never calls onInit(), so call it
    // directly here instead, the same way GhCalls does.
    connectedCallback() {
        if(this._callProfileInitialized) return;
        this._callProfileInitialized = true;

        this.waitForActiveUser().then(() => this.onInit()).catch((err) => {
            console.error('GhCallProfile init failed', err);
        });
    }

    //********************* WAIT FOR ACTIVE USER ********************************//
    waitForActiveUser() {
        if(window.gudhub?.storage?.user?.user_id) return Promise.resolve();

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if(window.gudhub?.storage?.user?.user_id) {
                    clearInterval(interval);
                    resolve();
                }
            }, 50);
        });
    }

    async onInit() {
        this.activeUserId = String(gudhub.storage.user.user_id);
        this.viewMode = 'picker';
        this.contacts = [];
        this.selectedUser = null;
        this.keypadDigits = KEYPAD_DIGITS;
        this.dialedNumber = '';
        this.callHistory = [];
        this.historyFilter = 'all';
        this.playingCallHistoryId = null;
        this.messages = [];
        this.sipTrunkConfigured = false;
        this.lines = [];
        this.selectedLineIndex = 1;
        this.smsSettingConfigured = false;
        this.smsLines = [];
        this.selectedSmsLineIndex = 1;
        this.sendError = null;
        this.itemPhoneNumbers = []; // [{fieldId, fieldName, value}] - the open item's Connected App numbers, if any
        this.selectedItemPhoneFilter = null; // null = unscoped History ("None"), or one of itemPhoneNumbers' values

        // gh-call-profile mounts hidden on every page load (it's part of
        // gudhub-messenger's always-rendered template) - loadContacts() (the
        // full sharing-group fan-out) is deliberately NOT awaited here, so it
        // doesn't fire until someone actually opens the Phone tab. showPicker()
        // (called from gh_conversation.js's showPhoneTab) loads it lazily on
        // first real use, and loadContacts() is idempotent so this can't
        // double-fetch if showPicker() runs before loadCallableLines() settles.
        await this.loadCallableLines();
        this.renderView();
    }

    //********************* LOAD CONTACTS ********************************//
    // Fetches the contact list once and caches it - both showPicker() and the
    // initial mount reuse this cache instead of refetching.
    async loadContacts() {
        if(this.contacts.length) return;
        this.contacts = await getOrgContacts(this.activeUserId, [this.activeUserId]);
    }

    //********************* LOAD CALLABLE LINES ********************************//
    // Gates the keypad's dial pad/call button and the Messages tab's composer,
    // and populates both tabs' own Line dropdowns. GET /sip-trunks/callable is
    // did_numbers-scoped (which trunks this user can actually place a call/send
    // SMS through), NOT the owner/admin-scoped GET /sip-trunks - an owner/admin
    // with no DID of their own would otherwise see an enabled keypad where every
    // call fails server-side (see selectOutboundTrunk in sip-gudhub-server's
    // dialplan.js). One request now covers both tabs - SMS used to be a fully
    // separate resource (GET /sms-settings/callable) since it had no relation to
    // sip_trunks, but now that sms lives as an embedded property of a trunk (see
    // sip-gudhub-server's docs/sip-trunks/sms.md), smsLines is just the
    // sms_enabled subset of the same response. Array order matches the server's
    // own dial-prefix indexing 1:1 (see selectLine/callDialedNumber below), so
    // lines is used as-is, not re-sorted; smsLines keeps that same relative
    // order too. Treat a fetch failure the same as "no line configured" rather
    // than silently leaving either tab usable.
    async loadCallableLines() {
        try {
            const trunks = await sipTrunksRequest('/sip-trunks/callable');

            this.lines = trunks;
            this.sipTrunkConfigured = this.lines.length > 0;
            if(this.selectedLineIndex > this.lines.length) this.selectedLineIndex = 1;

            this.smsLines = trunks.filter((trunk) => trunk.sms_enabled);
            this.smsSettingConfigured = this.smsLines.length > 0;
            if(this.selectedSmsLineIndex > this.smsLines.length) this.selectedSmsLineIndex = 1;
        } catch(err) {
            console.error('Failed to load callable lines', err);
            this.lines = [];
            this.sipTrunkConfigured = false;
            this.smsLines = [];
            this.smsSettingConfigured = false;
        }
    }

    //********************* SHOW USER ********************************//
    // Called by gudhub-messenger's Phone tab handler when a DM is the active
    // conversation - shows that contact's profile directly.
    // * otherUser (Object) — { user_id, fullname, avatar_128, avatar_512 }
    showUser(otherUser) {
        this.viewMode = 'profile';
        this.selectedUser = otherUser;
        this.renderView();
    }

    //********************* SHOW PICKER ********************************//
    // Called by gudhub-messenger's Phone tab handler when no DM is active,
    // and by the tab row's Contacts icon. Deliberately doesn't clear
    // selectedUser - the Profile tab (showProfileTab) needs it to still
    // point at whoever's active after the user browses through Contacts/
    // Keypad/History/Messages and back.
    async showPicker() {
        this.viewMode = 'picker';
        await this.loadContacts();
        this.renderView();
    }

    //********************* SHOW PROFILE TAB ********************************//
    // The tab row's Profile icon - shows the last-selected contact if there
    // is one, otherwise falls back to the contacts picker.
    showProfileTab() {
        if(this.selectedUser) {
            this.showUser(this.selectedUser);
        } else {
            this.showPicker();
        }
    }

    //********************* SHOW KEYPAD ********************************//
    showKeypad() {
        this.viewMode = 'keypad';
        this.renderView();
    }

    //********************* SHOW ITEM PHONE NUMBERS ********************************//
    // Called by gudhub-messenger's Phone tab handler when the current open item
    // has one or more Connected App phone numbers - shows a pill per number
    // (mirrors the legacy twilio-phone-panel's phonesList pills, see
    // gh_elements/twilio_phone/twilio_phone_panel.js) and dials the first one
    // by default, same as that panel's own defaultNumder pick.
    // * numbers (Array<{fieldId, fieldName, value}>)
    showItemPhoneNumbers(numbers) {
        this.viewMode = 'keypad';
        this.itemPhoneNumbers = numbers;
        // Matches the auto-dialed number below, so its own pill shows as the
        // active one instead of "None" - "None" is for when the user has
        // explicitly cleared the scoping/dialed number, not the default state.
        this.selectedItemPhoneFilter = numbers[0]?.value || null;
        this.dialedNumber = numbers[0]?.value || '';
        this.renderView();
    }

    //********************* SELECT ITEM PHONE NUMBER ********************************//
    // A pill click - the same pill row is rendered on the Keypad, History and
    // Messages tabs (see buildNumberPillsHtml). A specific number both dials
    // it (shared dialedNumber, also the Messages "To:" field) and scopes
    // History to it (see filterCallHistory); 'none' clears dialedNumber
    // instead - it isn't a single number to dial/message, and an empty
    // dialedNumber is already what shows every counterpart on Messages
    // (loadMessagesThread) and every call in History (unscoped).
    // * value (string) - a phone value from itemPhoneNumbers, or 'none'
    selectItemPhoneNumber(element, value) {
        if(value === 'none') {
            this.selectedItemPhoneFilter = null;
            this.dialedNumber = '';
        } else {
            this.selectedItemPhoneFilter = value;
            this.dialedNumber = value;
        }

        if(this.viewMode === 'messages') {
            this.loadMessagesThread().then(() => this.renderView());
        } else {
            this.renderView();
        }
    }

    //********************* SHOW HISTORY ********************************//
    // Fetched once and cached, same pattern as loadContacts(). Resets any
    // open recording player - it'd otherwise be re-rendered with a stale
    // playingCallHistoryId and autoplay again on return to this tab, since
    // the <audio> element was unmounted while another tab was showing.
    async showHistory() {
        this.viewMode = 'history';
        this.playingCallHistoryId = null;
        if(!this.callHistory.length) await this.loadCallHistory();
        this.renderView();
    }

    //********************* SHOW MESSAGES ********************************//
    async showMessages() {
        this.viewMode = 'messages';
        this.sendError = null;
        await this.loadMessagesThread();
        this.renderView();
    }

    //********************* DIAL DIGIT ********************************//
    dialDigit(element, digit) {
        this.dialedNumber += digit;
        this.renderView();
    }

    //********************* DELETE DIGIT ********************************//
    deleteDigit() {
        this.dialedNumber = this.dialedNumber.slice(0, -1);
        this.renderView();
    }

    //********************* SELECT LINE ********************************//
    // GhHtmlElement's onchange wiring always passes the DOM element that
    // fired the event as the first arg (see render() in GhHtmlElement.js) -
    // reading element.value here, not an attribute-string arg like
    // dialDigit's, since the selected <option> is only known at change time,
    // not when the template was last rendered. The value is 1-based,
    // matching its option's position in `lines` (see loadCallableLines) -
    // callDialedNumber below sends it straight through as the server's "*N"
    // dial-prefix index.
    selectLine(element) {
        this.selectedLineIndex = Number(element.value);
        this.renderView();
    }

    //********************* SELECT SMS LINE ********************************//
    // Mirrors selectLine() above, for the Messages tab's own line dropdown.
    // Changing which SMS setting is active means a different conversation
    // history, so this reloads the thread instead of just re-rendering.
    selectSmsLine(element) {
        this.selectedSmsLineIndex = Number(element.value);
        this.loadMessagesThread().then(() => this.renderView());
    }

    //********************* CALL DIALED NUMBER ********************************//
    // Dispatches the same start_call event gh-calls listens for (see
    // placeCall() below), passing the dialed number as callee_id. The
    // outgoing-call path (callSessionMixin.js) is built around GudHub user
    // ids - whether a raw PSTN number actually routes out through a SIP
    // trunk is a server/dialplan concern outside this component.
    callDialedNumber() {
        if(!this.dialedNumber) return;

        // The "*N" trunk-select prefix only means something to the server's
        // outbound-PSTN dialplan branch (dialplan.js), which only ever looks
        // at it ahead of a "+"-prefixed E.164 number - and only when there's
        // an actual choice among more than one of this user's own lines.
        const callee_id = this.lines.length > 1 && this.dialedNumber.startsWith('+')
            ? `*${this.selectedLineIndex}${this.dialedNumber}`
            : this.dialedNumber;

        this.dispatchEvent(new CustomEvent('start_call', {
            bubbles: true,
            composed: true,
            detail: { callee_id, caller_id: this.activeUserId, video: false }
        }));
    }

    //********************* SEND MESSAGE ********************************//
    // Reads the composer textarea directly rather than tracking its value as
    // reactive state - same reason wireKeypadNumberInput()/wireSearch() don't
    // either (renderView() re-renders the whole element on every change,
    // which would drop the textarea's focus/cursor on each keystroke if the
    // draft were reactive). Requires dialedNumber (the "To:" field, shared
    // with the Keypad tab) - sending with no destination isn't meaningful,
    // and the Send button is disabled in that state too (see
    // callProfile.html) so this is a belt-and-suspenders guard, not the
    // user's only feedback that a number is required.
    async sendMessage() {
        const textarea = this.querySelector('.call_profile__message_input');
        const text = (textarea?.value || '').trim();
        const trunkId = this.smsLines[this.selectedSmsLineIndex - 1]?.trunk_id;
        if(!text || !this.dialedNumber || !trunkId) return;

        this.sendError = null;

        try {
            // send-sms responds 200 even when the PROVIDER rejected the
            // message (e.g. Twilio 30034 "Message from an Unregistered
            // Number" for US A2P 10DLC) - only a request-level failure
            // (bad auth, sms not enabled) throws. Ignoring result.success
            // here made a rejected send look identical to a delivered one -
            // nothing in the UI ever showed the provider's own error.
            const result = await sipTrunksRequest(`/sip-trunks/${trunkId}/send-sms`, {
                method: 'POST',
                body: JSON.stringify({ to: this.dialedNumber, text })
            });

            if(!result.success) {
                this.sendError = result.result?.message || 'Message was not accepted by the provider';
            }

            if(textarea) textarea.value = '';
            await this.loadMessagesThread();
            this.renderView();
        } catch(err) {
            console.error('Failed to send SMS', err);
            this.sendError = err.message || 'Failed to send message';
            this.renderView();
        }
    }

    //********************* FORMAT RELATIVE DATE ********************************//
    formatRelativeDate(date) {
        return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    //********************* FORMAT DURATION ********************************//
    // Seconds -> "m:ss".
    formatDuration(seconds) {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${String(secs).padStart(2, '0')}`;
    }

    //********************* LOAD CALL HISTORY ********************************//
    // Fetches this user's point-to-point call history from sip-gudhub-server
    // and maps it into the shape callProfile.html renders. dateLabel/
    // durationLabel are precomputed here (rather than formatted in the
    // template) since GhHtmlElement's render() only exposes this's own
    // enumerable properties to the template, not its prototype methods.
    async loadCallHistory() {
        try {
            const entries = await getCallHistory(this.activeUserId);
            this.callHistory = entries.map((entry) => this.mapCallHistoryEntry(entry));
        } catch(err) {
            console.error('Failed to load call history', err);
            this.callHistory = [];
        }
    }

    //********************* MAP CALL HISTORY ENTRY ********************************//
    // * entry (Object) — a raw /call-history API entry, see
    // sip-gudhub-server/docs/call-history/list-history.md
    mapCallHistoryEntry(entry) {
        const isOutbound = String(entry.from) === this.activeUserId;
        const otherUserId = isOutbound ? entry.to : entry.from;
        const contact = this.contacts.find((c) => String(c.user_id) === String(otherUserId));
        const duration = entry.duration ? Number(entry.duration) : 0;

        return {
            callHistoryId: entry.call_history_id,
            type: entry.type,
            direction: isOutbound ? 'outbound' : 'inbound',
            number: contact?.fullname || otherUserId,
            // Kept separate from `number` above, which is the contact's fullname
            // when the counterpart is a known GudHub user - filterCallHistory's
            // item-phone-number scoping needs the raw value to match against.
            rawCounterpart: otherUserId,
            status: entry.status === 'missed' ? 'missed' : 'completed',
            dateLabel: this.formatRelativeDate(new Date(entry.created_at)),
            duration,
            durationLabel: this.formatDuration(duration),
            recordingUrl: entry.recording_path ? getRecordingUrl(entry.call_history_id) : null
        };
    }

    //********************* REFRESH CALL HISTORY ********************************//
    async refreshCallHistory() {
        const button = this.querySelector('.call_profile__history_refresh');
        button?.classList.add('call_profile__history_refresh--spinning');
        await this.loadCallHistory();
        this.renderView();
    }

    //********************* SET HISTORY FILTER ********************************//
    // * filter (string) — 'all' | 'voicemail' | 'incoming' | 'missed' | 'outgoing'
    setHistoryFilter(element, filter) {
        this.historyFilter = filter;
        this.renderView();
    }

    //********************* FILTER CALL HISTORY ********************************//
    // Computed here rather than in the template - renderView()'s template
    // interpolation only has access to this's own properties, not its
    // prototype methods (see GhHtmlElement.render()), the same reason
    // dateLabel/durationLabel are precomputed in mapCallHistoryEntry().
    filterCallHistory() {
        const typeFiltered = this.callHistory.filter((entry) => {
            switch(this.historyFilter) {
                case 'voicemail': return entry.type === 'voicemail';
                case 'incoming': return entry.type !== 'voicemail' && entry.direction === 'inbound';
                case 'missed': return entry.type !== 'voicemail' && entry.direction === 'inbound' && entry.status === 'missed';
                case 'outgoing': return entry.type !== 'voicemail' && entry.direction === 'outbound';
                default: return true;
            }
        });

        // selectedItemPhoneFilter (set by selectItemPhoneNumber - a number
        // pill click) scopes History to that one number; null ("None") leaves
        // History unscoped, same as before this existed.
        if(!this.selectedItemPhoneFilter) return typeFiltered;

        const target = normalizeNumber(this.selectedItemPhoneFilter);
        return typeFiltered.filter((entry) => normalizeNumber(entry.rawCounterpart) === target);
    }

    //********************* PLAY RECORDING ********************************//
    // Replaces a history row entirely with the inline player (see
    // callProfile.html's history row ternary + wireHistoryPlayer() below).
    // * callHistoryId (string) — the row's entry.callHistoryId
    playRecording(element, callHistoryId) {
        this.playingCallHistoryId = callHistoryId;
        this.renderView();
    }

    //********************* STOP RECORDING ********************************//
    stopRecording() {
        this.playingCallHistoryId = null;
        this.renderView();
    }

    //********************* LOAD MESSAGES THREAD ********************************//
    // Default view is every SMS this user has sent/received on the selected
    // trunk's SMS line, across every counterpart - filling in the "To:" field
    // (see wireMessagesNumberInput below, shared state with the Keypad tab's
    // dialedNumber) narrows it down to just that counterpart's thread.
    // GET /sip-trunks/:id/conversations is already scoped server-side to
    // trunks this user owns/admins, so "all history" here is inherently
    // this user's own - no extra filtering needed for that part.
    async loadMessagesThread() {
        const trunkId = this.smsLines[this.selectedSmsLineIndex - 1]?.trunk_id;
        if(!this.smsSettingConfigured || !trunkId) {
            this.messages = [];
            return;
        }

        try {
            const conversations = await sipTrunksRequest(`/sip-trunks/${trunkId}/conversations`);

            const rawMessages = this.dialedNumber
                ? (conversations.find((c) => c.counterpart_number === this.dialedNumber)?.messages || [])
                : conversations
                    .flatMap((c) => c.messages.map((m) => ({ ...m, counterpart_number: c.counterpart_number })))
                    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

            this.messages = rawMessages.map((m) => ({
                direction: m.direction,
                body: m.text,
                dateLabel: this.formatRelativeDate(new Date(m.created_at)),
                counterpart: m.counterpart_number,
                // 'sent'/'received' on success, 'failed' when the provider
                // rejected an outbound message (see sendMessage()) - the
                // server logs it either way, so a rejected send is still
                // visible in history, not silently missing.
                failed: m.status === 'failed'
            }));
        } catch(err) {
            console.error('Failed to load SMS messages', err);
            this.messages = [];
        }
    }

    //********************* BUILD NUMBER PILLS HTML ********************************//
    // Precomputed rather than called from inside callProfile.html's template
    // literal - that template only has access to `this`'s own enumerable
    // properties, not its prototype methods (see filterCallHistory's own
    // comment) - so this string is built here and interpolated as-is on the
    // Keypad, History and Messages tabs (see selectItemPhoneNumber for what a
    // pill click does on each). Empty string when the open item has no
    // Connected App numbers, so none of the three tabs render anything.
    buildNumberPillsHtml() {
        if(!this.itemPhoneNumbers.length) return '';

        const pills = this.itemPhoneNumbers.map(entry => `
            <span class="call_profile__number_pill${this.selectedItemPhoneFilter === entry.value ? ' active' : ''}" onclick="selectItemPhoneNumber('${entry.value}')">${entry.fieldName}</span>
        `).join('');

        return `
            <div class="call_profile__number_pills">
                ${pills}
                <span class="call_profile__number_pill${!this.selectedItemPhoneFilter ? ' active' : ''}" onclick="selectItemPhoneNumber('none')">None</span>
            </div>
        `;
    }

    //********************* RENDER VIEW ********************************//
    renderView() {
        this.filteredCallHistory = this.filterCallHistory();
        this.numberPillsHtml = this.buildNumberPillsHtml();
        super.render(html);
        this.wireSearch();
        this.wireZeroKeyHold();
        this.wireKeypadNumberInput();
        this.wireHistoryPlayer();
        this.wireMessagesNumberInput();
        this.wireMessageTextarea();
    }

    //********************* WIRE SEARCH ********************************//
    // Filters the rendered contact rows in place (rather than re-rendering)
    // so the search input keeps focus while typing.
    wireSearch() {
        const input = this.querySelector('.call_profile__search_input');
        if(!input) return;

        input.addEventListener('input', () => {
            const query = input.value.trim().toLowerCase();
            this.querySelectorAll('.call_profile__contact_row').forEach((row) => {
                row.classList.toggle('hidden', !row.dataset.name.includes(query));
            });
        });
    }

    //********************* WIRE ZERO KEY HOLD ********************************//
    // Standard phone-dialer behavior: a quick tap on the "0" key dials "0",
    // holding it for 2s dials "+" instead. The "0" key has no onclick of its
    // own (see callProfile.html) - this is its only handler, to avoid also
    // firing a plain click's "0" alongside the held "+".
    //
    // The long-press firing calls dialDigit(), which renderView()s the whole
    // element - that replaces the "0" key with a new DOM node mid-gesture and
    // re-runs this method, wiring a brand new set of listeners on it. Without
    // pointer capture (releasing this held pointer isn't guaranteed to land
    // back on the original node - and even setPointerCapture wouldn't help,
    // since capture is auto-released the moment its element is detached),
    // the eventual pointerup fires on that new listener set instead of the
    // one that started the hold. A closure-local flag would look "fresh"
    // there and wrongly dial an extra "0" - tracking heldLongEnough on `this`
    // instead means every generation of listeners sees the same, correct value.
    wireZeroKeyHold() {
        const zeroKey = this.querySelector('[data-zero-key]');
        if(!zeroKey) return;

        const HOLD_MS = 1000;

        const startHold = () => {
            this._zeroKeyHeldLongEnough = false;
            this._zeroKeyHoldTimer = setTimeout(() => {
                this._zeroKeyHeldLongEnough = true;
                this.dialDigit(zeroKey, '+');
            }, HOLD_MS);
        };

        const endHold = () => {
            clearTimeout(this._zeroKeyHoldTimer);
            if(!this._zeroKeyHeldLongEnough) this.dialDigit(zeroKey, '0');
            this._zeroKeyHeldLongEnough = false;
        };

        const cancelHold = () => clearTimeout(this._zeroKeyHoldTimer);

        zeroKey.addEventListener('pointerdown', startHold);
        zeroKey.addEventListener('pointerup', endHold);
        zeroKey.addEventListener('pointerleave', cancelHold);
        zeroKey.addEventListener('pointercancel', cancelHold);
    }

    //********************* WIRE KEYPAD NUMBER INPUT ********************************//
    // Lets the number display be typed into and pasted into directly, not
    // just built up via dialDigit()/deleteDigit(). Updates dialedNumber in
    // place on every 'input' event (covers typing and pasting alike - paste
    // fires 'input' too) without calling renderView(), for the same reason
    // wireSearch() doesn't: a full re-render would drop focus/cursor
    // position on every keystroke. The delete icon's hidden state is
    // normally computed at render time, so it's toggled here directly too,
    // otherwise it wouldn't update while typing/pasting without also
    // tapping a keypad button.
    wireKeypadNumberInput() {
        const input = this.querySelector('.call_profile__keypad_number');
        if(!input) return;

        const deleteIcon = this.querySelector('.call_profile__keypad_delete');

        input.addEventListener('input', () => {
            this.dialedNumber = input.value;
            deleteIcon?.classList.toggle('call_profile__keypad_delete--hidden', !this.dialedNumber);
        });

        input.addEventListener('keydown', (event) => {
            if(event.key === 'Enter') this.callDialedNumber();
        });
    }

    //********************* WIRE MESSAGES NUMBER INPUT ********************************//
    // Lets the Messages tab's own "To:" field be typed into directly,
    // mirroring wireKeypadNumberInput() above - updates dialedNumber
    // (shared with the Keypad tab) on every 'input' without a full
    // re-render, to preserve typing focus. Unlike the keypad, this field
    // also drives which thread is shown/sent to - re-fetching on every
    // keystroke would hammer GET .../conversations, so that only happens on
    // blur/Enter instead. The Send button's `disabled` attribute is baked
    // into the template at render time, though, and would otherwise stay
    // stale (disabled) from before dialedNumber was empty - toggling it
    // directly here too, the same way wireKeypadNumberInput() toggles the
    // delete icon's hidden state, so filling in a number and immediately
    // clicking Send (without blurring/pressing Enter first) actually works.
    wireMessagesNumberInput() {
        const input = this.querySelector('.call_profile__messages_to');
        if(!input) return;

        const sendBtn = this.querySelector('.call_profile__send_btn');

        const reload = () => {
            this.loadMessagesThread().then(() => this.renderView());
        };

        input.addEventListener('input', () => {
            this.dialedNumber = input.value;
            if(sendBtn) sendBtn.disabled = !this.dialedNumber;
        });

        input.addEventListener('blur', reload);
        input.addEventListener('keydown', (event) => {
            if(event.key === 'Enter') reload();
        });
    }

    //********************* WIRE MESSAGE TEXTAREA ********************************//
    // Grows the composer with its content (up to the CSS max-height cap,
    // where it scrolls instead) rather than staying a fixed one-line box -
    // same direct-DOM approach as the other wire*Input methods, since resetting
    // .value/height via a re-render would drop focus/cursor on every keystroke.
    // Height must be reset to 'auto' before reading scrollHeight, otherwise a
    // shrink (e.g. deleting a wrapped line) would never be reflected - a
    // taller old height keeps scrollHeight pinned at that same taller value.
    wireMessageTextarea() {
        const textarea = this.querySelector('.call_profile__message_input');
        if(!textarea) return;

        const resize = () => {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        };

        textarea.addEventListener('input', resize);
        resize();
    }

    //********************* WIRE HISTORY PLAYER ********************************//
    // Wires the inline recording player's play/pause toggle and seek bar
    // directly (rather than updating via renderView() on every 'timeupdate'),
    // for the same reason wireSearch()/wireKeypadNumberInput() do - a full
    // re-render would tear down and recreate the <audio> element, restarting
    // playback several times a second.
    wireHistoryPlayer() {
        const audio = this.querySelector('.call_profile__history_audio');
        if(!audio) return;

        const toggle = this.querySelector('.call_profile__history_play_toggle');
        const seek = this.querySelector('.call_profile__history_seek');
        const currentTimeLabel = this.querySelector('.call_profile__history_time_current');
        const totalTimeLabel = this.querySelector('.call_profile__history_time_total');

        // Range inputs have no built-in "played" vs "unplayed" track styling
        // cross-browser, so the fill is painted by hand as a gradient split
        // at the current percentage - this is what actually makes the point
        // read as moving, not just the thumb's own position. Set as a CSS
        // var consumed by ::-webkit-slider-runnable-track/::-moz-range-track
        // (see callProfile.scss) rather than seek's own background, so the
        // fill stays on the thin track instead of stretching across the
        // input's full (larger, for an easier click target) hit area.
        const updateSeekFill = () => {
            const percent = seek.max > 0 ? (seek.value / seek.max) * 100 : 0;
            seek.style.setProperty('--seek-fill', `${percent}%`);
        };

        audio.addEventListener('loadedmetadata', () => {
            seek.max = audio.duration;
            totalTimeLabel.textContent = this.formatDuration(Math.floor(audio.duration));
            updateSeekFill();
        });

        audio.addEventListener('timeupdate', () => {
            seek.value = audio.currentTime;
            currentTimeLabel.textContent = this.formatDuration(Math.floor(audio.currentTime));
            updateSeekFill();
        });

        audio.addEventListener('play', () => toggle.innerHTML = PAUSE_ICON);
        audio.addEventListener('pause', () => toggle.innerHTML = PLAY_ICON);

        seek.addEventListener('input', () => {
            audio.currentTime = seek.value;
            updateSeekFill();
        });

        toggle.addEventListener('click', () => {
            if(audio.paused) audio.play(); else audio.pause();
        });

        updateSeekFill();
    }

    //********************* SELECT CONTACT ********************************//
    // * userId (string) — from the clicked contact row
    selectContact(element, userId) {
        const contact = this.contacts.find(c => String(c.user_id) === String(userId));
        if(contact) this.showUser(contact);
    }

    //********************* PLACE CALL ********************************//
    // Phone button on the profile view - dispatches the same start_call
    // event gh-calls already listens for (see gh_conversation.js), just now
    // one of two sources of it (see callDialedNumber() above). Video is
    // always started via the in-call toggle now (see callScreen.js's
    // camera button), not from the profile view.
    // * video (boolean) — always false from this component's own button
    placeCall(element, video) {
        if(!this.selectedUser) return;

        this.dispatchEvent(new CustomEvent('start_call', {
            bubbles: true,
            composed: true,
            detail: { callee_id: String(this.selectedUser.user_id), caller_id: this.activeUserId, video }
        }));
    }
}

if(!customElements.get('gh-call-profile')) {
    customElements.define('gh-call-profile', GhCallProfile);
}
